import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PageHeader,
  SectionCard,
  FieldLabel,
  WarningBanner,
  BottomActionBar,
  ApproverRoleBadge,
  useConfirm,
} from "./shared/components";
import {
  getEmployeeDetails,
  initiateNoDues,
  getCurrentEmpNo,
  formatDate,
  NoDuesSeparationMode,
  EmployeeDetails,
} from "./services/eSeparation.service";

const MODES: { label: string; value: NoDuesSeparationMode; icon: string; color: string }[] = [
  { label: "Termination", value: "Termination", icon: "ti-circle-x", color: "#C0282C" },
  { label: "Retirement", value: "Retirement", icon: "ti-sun", color: "#BA7517" },
  { label: "Death", value: "Death", icon: "ti-heart-broken", color: "#78746D" },
  { label: "Deputation", value: "Deputation", icon: "ti-arrows-exchange", color: "#185FA5" },
  { label: "Transfer-Out", value: "Transfer-Out", icon: "ti-arrow-right-circle", color: "#4A1A6E" },
];

const STEPS = [
  "System generates a No-Dues Case ID",
  "Department-wise tasks assigned based on employee category & posting",
  "All department nodal officers receive task notifications",
  "Tasks begin processing in parallel (not sequentially)",
  "HR monitors progress via No-Dues dashboard",
];

const NoDuesInitiate: React.FC = () => {
  const navigate = useNavigate();
  const [empNo, setEmpNo] = useState("");
  const [emp, setEmp] = useState<EmployeeDetails | null>(null);
  const [searching, setSearching] = useState(false);
  const [mode, setMode] = useState<NoDuesSeparationMode | "">("");
  const [dateOfLeaving, setDateOfLeaving] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const searchEmployee = async () => {
    if (!empNo.trim()) return toast.warning("Please enter an employee number.");
    setSearching(true);
    const res = await getEmployeeDetails(empNo);
    setSearching(false);
    if (res.data && res.data.emp_no) setEmp(res.data);
    else {
      setEmp(null);
      toast.error("Employee details not found.");
    }
  };

  const handleCancel = async () => {
    if (await confirm("Discard this initiation?", { title: "Cancel", confirmLabel: "Discard", tone: "danger" }))
      navigate(-1);
  };

  const handleSubmit = async () => {
    if (!emp) return toast.warning("Please search and select an employee.");
    if (!mode) return toast.warning("Please select a separation mode.");
    if (!dateOfLeaving) return toast.warning("Please select date of leaving.");
    if (!reason.trim()) return toast.warning("Please provide a reason for leaving.");
    if (!(await confirm(`Initiate No-Dues for ${emp.emp_name || empNo} (${mode})? This will assign department-wise tasks to all nodal officers in parallel.`, { title: "Confirm Initiation", confirmLabel: "Initiate" })))
      return;
    setSubmitting(true);
    const res = await initiateNoDues({
      emp_no: empNo,
      separation_mode: mode as NoDuesSeparationMode,
      date_of_leaving: formatDate(dateOfLeaving),
      reason_for_leaving: reason,
      initiated_by_emp_no: getCurrentEmpNo(),
    });
    setSubmitting(false);
    if (res.success) {
      toast.success("No-Dues case has been initiated. Department-wise tasks have been assigned to nodal officers.");
      setEmpNo("");
      setEmp(null);
      setMode("");
      setDateOfLeaving("");
      setReason("");
    } else {
      toast.error(res.message || "Failed to initiate clearance.");
    }
  };

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-0">
        <PageHeader
          title="Manual No-Dues Initiation"
          subtitle="For Termination, Retirement, Death, Deputation, Transfer-Out"
          right={<ApproverRoleBadge role="HR" label="HR Admin" />}
          onExit={() => navigate(-1)}
        />

        <WarningBanner
          type="warning"
          message="No-Dues for Resignation / Short Notice is automatically triggered after approval. Use this screen only for non-resignation separations."
        />

        {/* Section 1 — Employee Search */}
        <SectionCard index={0} title="Employee Search">
          <FieldLabel label="Employee Number" required />
          <div className="d-flex gap-2">
            <input
              className="form-control"
              placeholder="Enter employee number"
              inputMode="numeric"
              value={empNo}
              onChange={(e) => setEmpNo(e.target.value)}
            />
            <button className="btn btn-primary" onClick={searchEmployee} disabled={searching}>
              <i className="ti ti-search" />
            </button>
          </div>
          {emp && (
            <div className="d-flex align-items-center gap-3 mt-3 p-2 rounded" style={{ background: "#E1F5EE" }}>
              <span
                className="d-inline-flex align-items-center justify-content-center"
                style={{ width: 40, height: 40, borderRadius: 12, background: "#E6F1FB" }}
              >
                <i className="ti ti-user" style={{ color: "#185FA5" }} />
              </span>
              <div className="flex-grow-1">
                <div className="fw-semibold">{emp.emp_name}</div>
                <small className="text-muted">
                  {emp.emp_no} • {emp.designation}
                </small>
                <div className="small text-muted">{emp.posting}</div>
              </div>
              <i className="ti ti-circle-check" style={{ color: "#049123", fontSize: 20 }} />
            </div>
          )}
        </SectionCard>

        {/* Section 2 — Separation Mode */}
        <SectionCard index={1} title="Separation Mode" subtitle="Select the type of separation for this employee:">
          <div className="row g-2">
            {MODES.map((m) => (
              <div className="col-md-6" key={m.value}>
                <div
                  className="card border h-100"
                  style={{
                    borderRadius: 10,
                    cursor: "pointer",
                    borderColor: mode === m.value ? `${m.color}66` : undefined,
                    background: mode === m.value ? `${m.color}10` : undefined,
                  }}
                  onClick={() => setMode(m.value)}
                >
                  <div className="card-body d-flex align-items-center gap-2 py-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{ width: 30, height: 30, borderRadius: 8, background: `${m.color}26`, color: m.color }}
                    >
                      <i className={`ti ${m.icon}`} />
                    </span>
                    <span className="fw-medium" style={{ color: mode === m.value ? m.color : undefined }}>
                      {m.label}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Section 3 — Details */}
        <SectionCard index={2} title="Separation Details">
          <div className="row g-3">
            <div className="col-md-6">
              <FieldLabel label="Date of Leaving" required />
              <input type="date" className="form-control" value={dateOfLeaving} onChange={(e) => setDateOfLeaving(e.target.value)} />
            </div>
            <div className="col-12">
              <FieldLabel label="Reason for Leaving" required />
              <textarea className="form-control" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Provide detailed reason..." />
            </div>
          </div>
        </SectionCard>

        {/* Info */}
        <SectionCard index={3} title="What happens when you initiate?">
          {STEPS.map((s, i) => (
            <div key={i} className="d-flex gap-2 mb-2">
              <span
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold"
                style={{ width: 22, height: 22, borderRadius: 11, background: "#E6F1FB", color: "#185FA5", fontSize: 11 }}
              >
                {i + 1}
              </span>
              <span className="small">{s}</span>
            </div>
          ))}
        </SectionCard>
      </div>

      <BottomActionBar
        primaryLabel="Initiate No-Dues"
        primaryIcon="ti-player-play"
        secondaryLabel="Cancel"
        onPrimary={handleSubmit}
        onSecondary={handleCancel}
        disabled={!emp || !mode || !dateOfLeaving || !reason.trim() || submitting}
      />
    </div>
  );
};

export default NoDuesInitiate;
